import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import client from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Flame, Dumbbell, Heart, Activity, Skull } from "lucide-react";

const goals = [
    { id: "lose_weight", title: "Lose Weight", desc: "Burn fat, get lean", icon: Flame, color: "#FF3B30" },
    { id: "build_strength", title: "Build Strength", desc: "Push your limits", icon: Dumbbell, color: "#39FF14" },
    { id: "improve_fitness", title: "Improve Fitness", desc: "Run further, breathe easier", icon: Heart, color: "#FF3B30" },
    { id: "stay_active", title: "Stay Active", desc: "Daily movement, no excuses", icon: Activity, color: "#39FF14" },
    { id: "challenge_mode", title: "Challenge Mode", desc: "Brutal. Elite-tier only.", icon: Skull, color: "#FF2424" },
];

export default function Onboarding() {
    const { setUser } = useAuth();
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const submit = async () => {
        if (!selected) return;
        setLoading(true);
        try {
            const { data } = await client.post("/users/me/goal", { goal: selected });
            setUser(data);
            toast.success("Locked in. Time to train.");
            navigate("/app");
        } catch (e) {
            toast.error("Could not save goal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-shell min-h-screen px-6 pt-10 pb-32">
            <span className="label-cap text-neon-green text-xs">Step 1 / 1</span>
            <h1 className="font-display font-black text-5xl uppercase tracking-tight mt-2">Pick Your Mission</h1>
            <p className="text-zinc-400 mt-2 mb-8">Choose what you're chasing. We'll calibrate the grind.</p>

            <div className="space-y-3">
                {goals.map((g, i) => (
                    <motion.button
                        key={g.id}
                        data-testid={`goal-${g.id}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.06 }}
                        onClick={() => setSelected(g.id)}
                        className={`w-full text-left p-4 flex items-center gap-4 transition-all ${
                            selected === g.id
                                ? "surface-2 border-2"
                                : "surface-1 hover:border-charcoal-400"
                        }`}
                        style={selected === g.id ? { borderColor: g.color, boxShadow: `0 0 20px ${g.color}40` } : {}}
                    >
                        <div
                            className="w-12 h-12 flex items-center justify-center"
                            style={{ background: `${g.color}20`, border: `1px solid ${g.color}40` }}
                        >
                            <g.icon size={22} style={{ color: g.color }} />
                        </div>
                        <div className="flex-1">
                            <div className="font-display font-bold uppercase tracking-tight text-lg leading-none">
                                {g.title}
                            </div>
                            <div className="text-zinc-400 text-sm mt-1">{g.desc}</div>
                        </div>
                    </motion.button>
                ))}
            </div>

            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] p-4 bg-charcoal-900/95 backdrop-blur-md border-t border-charcoal-500">
                <button
                    data-testid="onboarding-submit"
                    onClick={submit}
                    disabled={!selected || loading}
                    className="btn-primary w-full py-4 text-lg disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {loading ? "Locking..." : "Lock It In"}
                </button>
            </div>
        </div>
    );
}
